import { Pencil, Plus } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Button, Card, Col, Nav, Row, Table } from 'react-bootstrap'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import { ActiveInActive } from '../../../components/atoms/StatusLabel'
import TableAction from '../../../components/atoms/Table/TableAction'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import ModalAddDriver from '../../../components/modal/driver/ModalAddDriver'
import ModalEditDriver from '../../../components/modal/driver/ModalEditDriver'
import FilePreviewModal from '../../../components/modal/common/FilePreviewModal'
import api from '../../../config/AxiosInstance'
import '../../../styles/style.css'
import * as Common from '../../../utils/Common'

const DRIVER_TYPE = {
  INTERNAL: 'internal',
  EXTERNAL: 'external'
}

const tableHeaderStyle = {
  backgroundColor: '#f8f9fa',
  color: '#333333',
  fontWeight: '700',
  borderBottom: '1px solid #e9ecef',
  textTransform: 'uppercase',
  fontSize: '0.85rem'
}

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableStyle = {
  fontSize: '15px'
}

const MasterDriver = () => {
  const [modalAdd, setModalAdd] = useState(false)
  const [modalEdit, setModalEdit] = useState(false)
  const [editId, setEditId] = useState(0)
  const [drivers, setDrivers] = useState([])
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })
  const [loading, setLoading] = useState(false)
  const [activeKey, setActiveKey] = useState(DRIVER_TYPE.INTERNAL)

  const [showPreview, setShowPreview] = useState(false)
  const [previewUrl, setPreviewUrl] = useState('')
  const [previewName, setPreviewName] = useState('')
  const [previewType, setPreviewType] = useState('')

  const { per_page, current_page } = meta
  const abortControllerRef = useRef(null)

  useEffect(() => {
    fetchDrivers()
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [per_page, current_page, activeKey])

  const fetchDrivers = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    setLoading(true)
    try {
      const res = await api.get(
        `master/driver/list${
          activeKey === DRIVER_TYPE.EXTERNAL ? '-external' : ''
        }`,
        {
          signal,
          params: { per_page, page: current_page }
        }
      )
      const { metadata, data } = res.data
      setMeta(metadata)
      setDrivers(data)
    } catch (err) {
      if (
        err.name !== 'CanceledError' &&
        err.message !== 'canceled' &&
        err.code !== 'ERR_CANCELED'
      ) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    } finally {
      if (!signal.aborted) {
        setLoading(false)
      }
    }
  }

  const handleOnChangeMeta = (e) => {
    const perPageValue = parseInt(e.target.value, 10)
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: perPageValue,
      current_page: 1
    }))
  }

  const handlePageChange = (data) => {
    const selectedPage = data.selected + 1
    setMeta((prevMeta) => ({ ...prevMeta, current_page: selectedPage }))
    window.scrollTo(0, 0)
  }

  const handleSelectActive = (key) => {
    setActiveKey(key)
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleEdit = (id) => {
    setModalEdit(true)
    setEditId(id)
  }

  const handleShowPreview = (url, name, type = '') => {
    if (!url) return
    setPreviewUrl(url)
    setPreviewName(name || 'File Preview')
    setPreviewType(type)
    setShowPreview(true)
  }

  return (
    <>
      <div className="container-fluid px-4">
        <Row className="mb-3 align-items-center">
          <Col>
            <h3 style={headerTitleStyle}>Master Driver</h3>
          </Col>
          <Col className="text-end">
            <Button
              className="btn btn-primary-orange shadow-sm rounded px-4"
              onClick={() => setModalAdd(true)}
            >
              <Plus size={16} className="me-2" />
              Add Driver
            </Button>
          </Col>
        </Row>

        <Row className="mb-3">
          <Col>
            <Nav
              variant="underline"
              onSelect={handleSelectActive}
              defaultActiveKey={activeKey}
              className="ml-2 border-bottom"
            >
              <Nav.Item>
                <Nav.Link
                  eventKey={DRIVER_TYPE.INTERNAL}
                  className={`fw-semibold ${
                    activeKey === DRIVER_TYPE.INTERNAL
                      ? 'text-primary'
                      : 'text-muted'
                  }`}
                >
                  Internal
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link
                  eventKey={DRIVER_TYPE.EXTERNAL}
                  className={`fw-semibold ${
                    activeKey === DRIVER_TYPE.EXTERNAL
                      ? 'text-primary'
                      : 'text-muted'
                  }`}
                >
                  External
                </Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={12}>
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
              <Table responsive hover className="mb-0 b-table-frameless">
                <thead style={tableHeaderStyle}>
                  <tr>
                    <th className="p-2 align-middle">Name</th>
                    <th className="p-2 align-middle">
                      {activeKey === DRIVER_TYPE.INTERNAL ? 'Address' : 'SIM'}
                    </th>
                    <th className="p-2 align-middle">
                      {activeKey === DRIVER_TYPE.INTERNAL
                        ? 'Phone Number'
                        : 'KTP'}
                    </th>
                    <th className="p-2 align-middle">Status</th>
                    <th className="p-2 align-middle text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="text-center p-5">
                        <Loader />
                      </td>
                    </tr>
                  ) : drivers.length === 0 ? (
                    <tr>
                      <td colSpan={5}>
                        <RowEmpty colSpan={5} />
                      </td>
                    </tr>
                  ) : (
                    drivers.map((data, i) => (
                      <tr key={i}>
                        <td
                          className="p-2 align-middle text-dark fw-medium"
                          style={tableStyle}
                        >
                          {data?.name}
                        </td>
                        <td
                          className="p-2 align-middle text-muted"
                          style={tableStyle}
                        >
                          {activeKey === DRIVER_TYPE.INTERNAL ? (
                            data?.address
                          ) : (
                            <span
                              className="text-primary cursor-pointer text-decoration-underline"
                              onClick={() =>
                                handleShowPreview(
                                  data?.sim_doc,
                                  `SIM - ${data?.name}`
                                )
                              }
                              style={{ cursor: 'pointer' }}
                            >
                              {data.sim_doc
                                ? data.sim_doc.split('/').pop()
                                : '-'}
                            </span>
                          )}
                        </td>
                        <td
                          className="p-2 align-middle text-muted"
                          style={tableStyle}
                        >
                          {activeKey === DRIVER_TYPE.INTERNAL ? (
                            data?.phone_number
                          ) : (
                            <span
                              className="text-primary cursor-pointer text-decoration-underline"
                              onClick={() =>
                                handleShowPreview(
                                  data?.ktp_doc,
                                  `KTP - ${data?.name}`
                                )
                              }
                              style={{ cursor: 'pointer' }}
                            >
                              {data?.ktp_doc
                                ? data?.ktp_doc.split('/').pop()
                                : '-'}
                            </span>
                          )}
                        </td>
                        <td className="p-2 align-middle">
                          <ActiveInActive
                            text={data.status ? 'active' : 'inactive'}
                          />
                        </td>
                        <td className="p-2 align-middle text-end">
                          <TableAction>
                            <button
                              className="btn btn-light shadow-sm rounded d-flex align-items-center justify-content-center p-0"
                              style={{ width: '32px', height: '32px' }}
                              onClick={() => handleEdit(data?.id)}
                              aria-label={`Edit ${data?.name}`}
                            >
                              <Pencil size={13} />
                            </button>
                          </TableAction>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
              {drivers.length > 0 && (
                <div className="p-3 border-top bg-white">
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
      <ModalAddDriver
        show={modalAdd}
        onHide={() => setModalAdd(false)}
        refreshData={fetchDrivers}
        activeKey={activeKey}
      />
      <ModalEditDriver
        show={modalEdit}
        onHide={() => setModalEdit(false)}
        id={editId}
        refreshData={fetchDrivers}
        activeKey={activeKey}
      />
      <FilePreviewModal
        show={showPreview}
        onHide={() => setShowPreview(false)}
        fileUrl={previewUrl}
        fileName={previewName}
        fileType={previewType}
      />
    </>
  )
}

export default MasterDriver
